import { Link } from 'wouter';
import { Wordmark, BrandLogo } from './components';
import { NewsletterSignup } from './components/NewsletterSignup';
import { SocialLinksFooter } from './components/SocialLinks';
import { getLastLandingPage } from './audience';
import { FREE_TOOL_PAGES } from '../../../methods/src/common/publicPages';
import { reopenPreferences } from './consent/consentManager';

/**
 * The standard commercial-app footer. Lives at the bottom of every page —
 * authenticated and public alike — so the brand and legal links are always
 * one click away. Replaces the old single-line disclaimer footer.
 *
 * Structure:
 *   - Top row: brand wordmark + tagline | three columns of links
 *   - Bottom rule: legal disclaimer (kept from the old footer) + copyright
 *
 * The link columns are intentionally small and curated. We don't try to
 * link every page; just the ones a visitor would expect to find: product
 * surfaces, company info, and legal docs.
 */
export function SiteFooter() {
  const year = new Date().getFullYear();
  // Route the footer wordmark back to whichever landing page the visitor
  // most recently came from in THIS tab. Tab-scoped, so visitors browsing
  // from / are returned to / even if they previously visited /students
  // in a separate session. Same pattern as PublicShell, DemoMemo.tsx.
  const brandHref = getLastLandingPage();
  return (
    <footer className="site-footer" role="contentinfo">
      {/* Newsletter band — sits above the main footer rows. The largest
          conversion lever on a public marketing page. Captures emails
          from visitors who don't sign up but are interested. */}
      <div className="site-footer__newsletter">
        <div className="site-footer__newsletter-copy">
          {/* Heading uses the actual EntreDash wordmark (on the light footer),
              framed by "The" / "Newsletter" in a quiet sans — smaller and less
              shouty than the former big-serif "The EntreDash Newsletter". */}
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '0.32em',
              flexWrap: 'wrap',
              fontSize: 'clamp(17px, 1.5vw, 21px)',
              marginBottom: 10,
            }}
          >
            <span style={{ fontFamily: 'var(--f-sans)', fontWeight: 600, color: 'var(--ink)' }}>
              The
            </span>
            <Wordmark size="inherit" />
            <span style={{ fontFamily: 'var(--f-sans)', fontWeight: 600, color: 'var(--ink)' }}>
              Newsletter
            </span>
          </div>
          <p style={{ fontSize: 14.5, lineHeight: 1.6, margin: 0, maxWidth: 520 }}>
            New features, practical tips, and honest writing on entrepreneurship,
            IP, product development, and commercialization. For everyday
            inventors and founders. No spam. Unsubscribe in one click.
          </p>
        </div>
        <div className="site-footer__newsletter-form">
          <NewsletterSignup
            source="landing_footer"
            variant="wide"
            // Inline wordmark so the brand mark renders properly here
            // rather than as plain text. Same treatment used everywhere
            // else "MeInvent" appears in user-visible copy.
            helpText={
              <>
                By subscribing, you agree to receive occasional emails
                from <Wordmark size="inherit" />.
              </>
            }
          />
        </div>
      </div>

      <div className="site-footer__top">
        <div className="site-footer__brand">
          {/* Brand wordmark links to /landing (or /students for
              audience=student visitors — see `brandHref` above) — a
              route that always renders the public marketing page
              regardless of auth state. Logged-in users can revisit
              without being bounced to /dashboard. */}
          {/* Full-scale brand sign-off — the page's "back cover" brand
              moment. Large wordmark + the north-star line, with the
              companion sentence as supporting copy. */}
          {/* The footer is the place the full CANONICAL lockup appears:
              mark + wordmark, with the two taglines justified beneath the
              wordmark — "Spark to business" (brass, the arc) on the left,
              "Invent, by Me" (uniform heartwood ownership line) on the right.
              The page's "back cover" brand moment. The ™ is NOT on this lockup;
              it appears once, on the copyright fine-print line below. */}
          <Link
            href={brandHref}
            aria-label="View homepage"
            className="site-footer__brandmark"
            style={{ textDecoration: 'none' }}
          >
            <BrandLogo size="inherit" tagline />
          </Link>
          <p className="site-footer__northstar">
            Where ideas become inventions, products, and businesses.
          </p>
        </div>

        <nav className="site-footer__cols" aria-label="Footer navigation">
          <div>
            <div className="site-footer__heading">Product</div>
            <ul>
              <li><Link href="/strategy">Strategy</Link></li>
              <li><Link href="/learn">Learn</Link></li>
              <li><Link href="/students">For students &amp; parents</Link></li>
              <li><Link href="/invention-concept-drawings">Invention concept drawings</Link></li>
              <li><Link href="/invention-3d-model">Invention 3D model</Link></li>
              <li><Link href="/how-to-develop-an-invention">How to develop an invention</Link></li>
              <li><Link href="/cost-to-develop-an-invention">Cost to develop an invention</Link></li>
              <li><Link href="/invention-help">Who can help with an invention</Link></li>
              <li><Link href="/prepare-for-patent-attorney">Prepare for a patent attorney</Link></li>
            </ul>
          </div>
          <div>
            <div className="site-footer__heading">Company</div>
            <ul>
              <li><Link href="/about">About</Link></li>
              {/* Full-page anchor (not Link): the blog is pre-rendered
                  static HTML served at a .html URL, not an SPA route. */}
              <li><a href="/blog.html">Blog</a></li>
              <li><Link href="/affiliates">Affiliates</Link></li>
              <li><Link href="/contact">Contact</Link></li>
              <li><Link href="/settings/feedback">Feedback</Link></li>
            </ul>
          </div>
          {/* Free Tools — derived from the public-page registry's `freeTool`
              tag (methods/src/common/publicPages.ts), so every free calculator
              or utility we ship appears here automatically, in registry order,
              with no separate hand-kept list to drift. These sitewide internal
              links also pass authority to the tool pages, which is the point of
              the free-tools SEO play. Rendered only when at least one exists. */}
          {FREE_TOOL_PAGES.length > 0 && (
            <div>
              <div className="site-footer__heading">Free Tools</div>
              <ul>
                {FREE_TOOL_PAGES.map((page) => (
                  <li key={page.path}>
                    <Link href={page.path}>{page.freeTool!.footerLabel}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="site-footer__heading">Legal</div>
            <ul>
              <li><Link href="/security">Trust &amp; Security</Link></li>
              <li><Link href="/terms">Terms of Service</Link></li>
              <li><Link href="/privacy">Privacy Policy</Link></li>
              <li><Link href="/cookies">Cookie Policy</Link></li>
              <li>
                <button
                  type="button"
                  className="site-footer__linkbtn"
                  onClick={() => reopenPreferences()}
                >
                  Cookie preferences
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </div>

      {/* Social row sits between the link grid and the legal/copyright
          bottom rule. The prior "FOLLOW [wordmark]" label was removed —
          the prominent icon row carries the follow affordance on its own
          (Instagram, Facebook, X, LinkedIn, Reddit live; WhatsApp pending). */}
      <div
        style={{
          padding: '36px 0 32px',
          borderTop: '1px solid var(--border-hairline)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SocialLinksFooter />
      </div>

      {/* Legal footer — compressed to TWO centered lines.
          Line 1: the educational-guidance disclaimer first, then legal status
          (Delaware incorporation), registered address, and toll-free phone.
          Line 2: the copyright / all-rights-reserved.
          "EntreDash" renders as PLAIN text (not the <Wordmark> lockup) so the
          legal lines read as quiet fine print; the two-tone wordmark lives only
          in the back-cover lockup above. */}
      <div className="site-footer__legal">
        {/* Line 1: the educational-guidance disclaimer only. */}
        <p className="site-footer__legal-line">
          <span className="site-footer__disclaimer">
            EntreDash<sup className="site-footer__tm">™</sup> provides educational
            and strategic guidance, not legal advice. Consult a licensed patent
            practitioner before taking any binding action.
          </span>
        </p>
        {/* Line 2: legal status, registered address, phone, then copyright. */}
        <p className="site-footer__legal-line">
          EntreDash, Inc. · Incorporated in Delaware, United States
          <span className="site-footer__sep" aria-hidden="true">·</span>
          3723 Greenville Avenue, STE 45690, Dallas, TX 75206, USA
          <span className="site-footer__sep" aria-hidden="true">·</span>
          Toll-free <a href="tel:+18883284046">+1 888 328 4046</a>
          <span className="site-footer__sep" aria-hidden="true">·</span>
          © {year} EntreDash<sup className="site-footer__tm">™</sup>. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
